import React, { useState, useEffect, useCallback } from 'react';
import { 
  Machine, 
  UsageProfile, 
  DegradationPattern, 
  SimulationConfig, 
  TelemetryUpdate,
  SimulationStateUpdate,
  DemoPreset
} from '@cbm/shared';
import { useWebSocket, useWebSocketSubscription } from '../contexts/WebSocketContext';
import { DashboardContentShell, KPIBar, DashboardSection, DashboardWidget } from '../components/shared/layout/DashboardLayout';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { DemoPresets } from '../components/simulation/DemoPresets';
import { Play, Square, Zap, Settings, Activity, Clock, Cpu, AlertTriangle } from 'lucide-react';

const MOCK_MACHINES: Machine[] = [
  {
    pk: 'TENANT#demo',
    sk: 'MACHINE#M-001',
    displayName: 'Production Line A - Station 1',
    designVariant: 'Model X-200',
    usageProfile: 'normal',
    createdAt: new Date().toISOString(),
  },
  {
    pk: 'TENANT#demo',
    sk: 'MACHINE#M-002',
    displayName: 'Production Line A - Station 2',
    designVariant: 'Model X-200',
    usageProfile: 'aggressive',
    createdAt: new Date().toISOString(),
  },
  {
    pk: 'TENANT#demo',
    sk: 'MACHINE#M-003',
    displayName: 'Production Line B - Station 1',
    designVariant: 'Model Y-150',
    usageProfile: 'poor_pm',
    createdAt: new Date().toISOString(),
  },
];

const USAGE_PROFILES: { value: UsageProfile; label: string; description: string; color: string }[] = [
  { value: 'normal', label: 'Normal Operation', description: 'Standard operating conditions', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'aggressive', label: 'Aggressive Mode', description: 'High-intensity operation', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'poor_pm', label: 'Poor Maintenance', description: 'Deferred maintenance scenario', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

const DEGRADATION_PATTERNS: { value: DegradationPattern; label: string; description: string; color: string }[] = [
  { value: 'A', label: 'Pattern A - Mechanical Wear', description: 'Progressive bearing and spindle degradation', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  { value: 'B', label: 'Pattern B - Utility Instability', description: 'Air/vacuum system degradation', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'C', label: 'Pattern C - Operator Masking', description: 'Masked failures with delayed detection', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
];

function SimulationControls({
  selectedMachine,
  onMachineSelect,
  simulationConfig,
  onConfigChange,
  isRunning,
  onStart,
  onStop,
  onInjectEvent
}: {
  selectedMachine: Machine | null;
  onMachineSelect: (machine: Machine) => void;
  simulationConfig: SimulationConfig | null;
  onConfigChange: (config: Partial<SimulationConfig>) => void;
  isRunning: boolean;
  onStart: () => void;
  onStop: () => void;
  onInjectEvent: (eventType: string) => void;
}) {
  return (
    <div className="space-y-6">
      {/* Machine Selection */}
      <div>
        <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Target Machine</label>
        <select
          value={selectedMachine?.sk || ''}
          onChange={(e) => {
            const machine = MOCK_MACHINES.find(m => m.sk === e.target.value);
            if (machine) onMachineSelect(machine);
          }}
          disabled={isRunning}
          className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">Select target machine...</option>
          {MOCK_MACHINES.map((m) => (
            <option key={m.sk} value={m.sk}>{m.displayName}</option>
          ))}
        </select>
      </div>

      {selectedMachine && (
        <>
          {/* Usage Profile Selection */}
          <div>
            <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Operating Profile</label>
            <div className="space-y-3">
              {USAGE_PROFILES.map((profile) => (
                <label key={profile.value} className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="usageProfile"
                    value={profile.value}
                    checked={simulationConfig?.usageProfile === profile.value}
                    onChange={(e) => onConfigChange({ usageProfile: e.target.value as UsageProfile })}
                    className="mt-1 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-900"
                    disabled={isRunning}
                  />
                  <div className={`flex-1 p-3 border rounded-lg transition-all ${
                    simulationConfig?.usageProfile === profile.value 
                      ? profile.color 
                      : 'bg-slate-800 border-slate-700 group-hover:border-slate-600'
                  }`}>
                    <div className="font-medium text-white">{profile.label}</div>
                    <div className="text-xs text-slate-400 mt-1">{profile.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Degradation Pattern Selection */}
          <div>
            <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Failure Pattern</label>
            <div className="space-y-3">
              <label className="flex items-start space-x-3 cursor-pointer group">
                <input
                  type="radio"
                  name="degradationPattern"
                  value=""
                  checked={!simulationConfig?.degradationPattern}
                  onChange={() => onConfigChange({ degradationPattern: undefined })}
                  className="mt-1 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-900"
                  disabled={isRunning}
                />
                <div className={`flex-1 p-3 border rounded-lg transition-all ${
                  !simulationConfig?.degradationPattern 
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                    : 'bg-slate-800 border-slate-700 group-hover:border-slate-600'
                }`}>
                  <div className="font-medium text-white">Healthy Operation</div>
                  <div className="text-xs text-slate-400 mt-1">No degradation pattern applied</div>
                </div>
              </label>
              {DEGRADATION_PATTERNS.map((pattern) => (
                <label key={pattern.value} className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="degradationPattern"
                    value={pattern.value}
                    checked={simulationConfig?.degradationPattern === pattern.value}
                    onChange={(e) => onConfigChange({ degradationPattern: e.target.value as DegradationPattern })}
                    className="mt-1 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-900"
                    disabled={isRunning}
                  />
                  <div className={`flex-1 p-3 border rounded-lg transition-all ${
                    simulationConfig?.degradationPattern === pattern.value 
                      ? pattern.color 
                      : 'bg-slate-800 border-slate-700 group-hover:border-slate-600'
                  }`}>
                    <div className="font-medium text-white">{pattern.label}</div>
                    <div className="text-xs text-slate-400 mt-1">{pattern.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Timing Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Sample Rate</label>
              <div className="bg-slate-900 border border-slate-600 rounded-lg p-3">
                <input
                  type="range"
                  min="100"
                  max="5000"
                  step="100"
                  value={simulationConfig?.intervalMs || 1000}
                  onChange={(e) => onConfigChange({ intervalMs: parseInt(e.target.value) })}
                  className="w-full accent-blue-500"
                  disabled={isRunning}
                />
                <div className="flex justify-between text-xs text-slate-400 mt-2">
                  <span>Fast</span>
                  <span className="font-medium text-white">{simulationConfig?.intervalMs || 1000}ms</span>
                  <span>Slow</span>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">Duration</label>
              <div className="bg-slate-900 border border-slate-600 rounded-lg p-3">
                <input
                  type="number"
                  min="0"
                  max="1440"
                  value={simulationConfig?.duration ? Math.round(simulationConfig.duration / 60000) : 0}
                  onChange={(e) => {
                    const minutes = parseInt(e.target.value);
                    onConfigChange({ duration: minutes > 0 ? minutes * 60000 : undefined });
                  }}
                  className="w-full bg-transparent text-white focus:outline-none"
                  placeholder="∞"
                  disabled={isRunning}
                />
                <div className="text-xs text-slate-400 mt-1">Minutes (0 = infinite)</div>
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="space-y-3">
            <button
              onClick={isRunning ? onStop : onStart}
              disabled={!simulationConfig?.usageProfile}
              className={`w-full flex items-center justify-center space-x-2 px-6 py-4 rounded-lg font-medium text-lg transition-all ${
                isRunning
                  ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/25'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25 disabled:bg-slate-700 disabled:cursor-not-allowed disabled:shadow-none'
              }`}
            >
              {isRunning ? (
                <><Square className="w-5 h-5" /> <span>Stop Simulation</span></>
              ) : (
                <><Play className="w-5 h-5" /> <span>Start Simulation</span></>
              )}
            </button>

            {/* Event Injection */}
            {isRunning && (
              <div className="border-t border-slate-700 pt-4">
                <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Event Injection</label>
                <div className="grid grid-cols-1 gap-2">
                  <button 
                    onClick={() => onInjectEvent('utility_dip')} 
                    className="flex items-center space-x-2 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Zap className="w-4 h-4" />
                    <span>Utility Dip Event</span>
                  </button>
                  <button 
                    onClick={() => onInjectEvent('operator_reset')} 
                    className="flex items-center space-x-2 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Operator Reset</span>
                  </button>
                  <button 
                    onClick={() => onInjectEvent('jam_burst')} 
                    className="flex items-center space-x-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    <span>Jam Burst Event</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function LiveTelemetry({ machineId, isRunning }: { machineId: string | null; isRunning: boolean }) {
  const [rawTelemetryData, setRawTelemetryData] = useState<any[]>([]);
  const [stats, setStats] = useState({ sampleCount: 0, avgCycleTime: 0, avgTemperature: 0, avgVibration: 0 });
  const { subscribeToMachine, unsubscribeFromMachine } = useWebSocket();

  /**
   * Convert a TelemetrySample[] shape ({ timestamp, tags:[{name,value}...] })
   * into a Recharts-friendly wide time-series shape:
   *   { timestamp: <ms>, cycle_time: 12.3, temperature: 45.6, ... }
   *
   * This fixes the common failure mode where the chart mistakenly receives an
   * array of "tag" objects (X-axis becomes tag names) instead of time-series points.
   */
  const toChartSeries = useCallback((samples: any[]) => {
    return samples
      .filter(Boolean)
      .map((s) => {
        const ts = s?.timestamp ? new Date(s.timestamp).getTime() : Date.now();
        const point: Record<string, number> & { timestamp: number } = { timestamp: ts };

        const tags: any[] = Array.isArray(s?.tags) ? s.tags : [];
        for (const t of tags) {
          const name = String(t?.name ?? '').trim();
          const val = Number(t?.value);
          if (name) point[name] = Number.isFinite(val) ? val : 0;
        }
        return point;
      });
  }, []);

  const handleTelemetryUpdate = useCallback((data: TelemetryUpdate) => {
    const sample: any = (data as any)?.payload?.sample ?? (data as any)?.payload ?? {};

    const sampleMachineId =
      sample.machineId ??
      (data as any)?.payload?.machineId ??
      (data as any)?.machineId;

    if (!sampleMachineId || sampleMachineId !== machineId || !isRunning) return;

    let tags: any = sample.tags ?? sample.payload?.tags;
    if (!tags) return;

    if (!Array.isArray(tags)) {
      tags = Object.entries(tags).map(([name, value]) => ({ name, value }));
    }

    if (!tags.length) return;

    const timestamp = sample.timestamp ?? (data as any)?.timestamp ?? new Date().toISOString();

    setRawTelemetryData(prev => {
      const newData = [...prev, { timestamp, tags }].slice(-100);
      
      setStats({
        sampleCount: newData.length,
        avgCycleTime: newData.reduce((sum, s) => sum + (s.tags.find((t: any) => t.name === 'cycle_time')?.value || 0), 0) / newData.length,
        avgTemperature: newData.reduce((sum, s) => sum + (s.tags.find((t: any) => t.name === 'temperature')?.value || 0), 0) / newData.length,
        avgVibration: newData.reduce((sum, s) => sum + (s.tags.find((t: any) => t.name === 'vibration_level')?.value || 0), 0) / newData.length,
      });
      
      return newData;
    });
  }, [machineId, isRunning]);

  useWebSocketSubscription({ onTelemetryUpdate: handleTelemetryUpdate });

  useEffect(() => {
    if (machineId && isRunning) {
      subscribeToMachine(machineId);
      return () => unsubscribeFromMachine(machineId);
    }
  }, [machineId, isRunning, subscribeToMachine, unsubscribeFromMachine]);

  useEffect(() => {
    if (!isRunning) {
      setRawTelemetryData([]);
      setStats({ sampleCount: 0, avgCycleTime: 0, avgTemperature: 0, avgVibration: 0 });
    }
  }, [isRunning, machineId]);

  if (!machineId) {
    return (
      <div className="text-center py-12">
        <Cpu className="w-12 h-12 mx-auto text-slate-600 mb-3" />
        <p className="text-slate-400">Select a machine to view telemetry</p>
      </div>
    );
  }
  
  if (!isRunning) {
    return (
      <div className="text-center py-12">
        <Play className="w-12 h-12 mx-auto text-slate-600 mb-3" />
        <p className="text-slate-400">Start simulation to generate data</p>
      </div>
    );
  }
  
  if (rawTelemetryData.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
        <p className="text-slate-400">Waiting for telemetry data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Real-time Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Activity className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">Samples</span>
          </div>
          <div className="text-lg font-bold text-white">{stats.sampleCount}</div>
        </div>
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Clock className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">Cycle</span>
          </div>
          <div className="text-lg font-bold text-white">{stats.avgCycleTime.toFixed(1)}s</div>
        </div>
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="w-4 h-4 bg-orange-500 rounded-full" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">Temp</span>
          </div>
          <div className="text-lg font-bold text-white">{stats.avgTemperature.toFixed(1)}°C</div>
        </div>
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <span className="w-4 h-4 bg-purple-500 rounded-full" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">Vibe</span>
          </div>
          <div className="text-lg font-bold text-white">{stats.avgVibration.toFixed(2)} mm/s</div>
        </div>
      </div>
      
      {/* Chart */}
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-white">Live Telemetry Stream</h4>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-slate-400">Live • {rawTelemetryData.length} points</span>
          </div>
        </div>
        {rawTelemetryData.length > 0 ? (() => {
          const chartData = toChartSeries(rawTelemetryData);
          return (
            <LiveTelemetryStreamChart
              key={`${machineId ?? 'none'}-${chartData.length}`}
              data={chartData}
              height={400}
            />
          );
        })() : (
          <div className="flex items-center justify-center h-[400px] text-slate-500">No data</div>
        )}
      </div>
    </div>
  );
}

type LiveChartPoint = { timestamp: number } & Record<string, number>;

function LiveTelemetryStreamChart({
  data,
  height,
}: {
  data: LiveChartPoint[];
  height: number;
}) {
  const first = data[0] || { timestamp: Date.now() };
  const availableKeys = Object.keys(first).filter((k) => k !== 'timestamp');

  // Prefer the most meaningful PLC tags first (keeps the chart readable)
  const preferredOrder = [
    'cycle_time',
    'throughput',
    'temperature',
    'vibration_level',
    'power_consumption',
    'pressure',
    'air_flow',
    'coolant_flow',
    'spindle_speed',
    'bearing_temp',
  ];

  const seriesKeys = preferredOrder.filter((k) => availableKeys.includes(k));

  // If telemetry schema changes, still render *something*.
  const fallbackKeys = seriesKeys.length > 0 ? seriesKeys : availableKeys.slice(0, 4);

  const formatTime = (ts: number) => {
    try {
      return new Date(ts).toLocaleTimeString(undefined, { hour12: false });
    } catch {
      return String(ts);
    }
  };

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
          <XAxis
            dataKey="timestamp"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTime}
            tick={{ fontSize: 11 }}
          />
          <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={40} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={40} />
          <Tooltip
            labelFormatter={(v) => `Time: ${formatTime(Number(v))}`}
            formatter={(value: any, name: any) => [Number(value).toFixed(2), String(name)]}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />

          {/*
            Map key → axis:
              - power/throughput often large → right axis
              - most condition indicators → left axis
          */}
          {fallbackKeys.map((k) => {
            const yAxisId = k === 'power_consumption' || k === 'throughput' ? 'right' : 'left';
            return (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                yAxisId={yAxisId}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function SimulationPage() {
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [simulationConfig, setSimulationConfig] = useState<SimulationConfig | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'manual' | 'presets'>('manual');
  const [sessionStats, setSessionStats] = useState({ startTime: null as string | null, samplesGenerated: 0, eventsInjected: 0 });
  const { sendMessage, subscribeToMachine } = useWebSocket();

  useEffect(() => {
    if (!selectedMachine) {
      setSimulationConfig(null);
      return;
    }

    const machineId = selectedMachine.sk.replace('MACHINE#', '');
    setSimulationConfig(prev => ({
      ...(prev ?? { intervalMs: 1000 }),
      machineId,
      usageProfile: selectedMachine.usageProfile,
    }));
  }, [selectedMachine]);

  const handleSimulationStateUpdate = useCallback((data: SimulationStateUpdate) => {
    if (data.payload.machineId === simulationConfig?.machineId) {
      const wasRunning = isRunning;
      const nowRunning = data.payload.status === 'RUNNING';
      setIsRunning(nowRunning);
      
      if (!wasRunning && nowRunning) {
        setSessionStats(prev => ({ ...prev, startTime: new Date().toISOString(), samplesGenerated: 0, eventsInjected: 0 }));
      } else if (wasRunning && !nowRunning) {
        setSessionStats(prev => ({ ...prev, startTime: null }));
      }
    }
  }, [simulationConfig?.machineId, isRunning]);

  const handleTelemetryUpdate = useCallback((data: TelemetryUpdate) => {
    console.log('SimulationPage telemetry:', { 
      received: data.payload.machineId, 
      expected: simulationConfig?.machineId, 
      match: data.payload.machineId === simulationConfig?.machineId,
      isRunning 
    });
    if (data.payload.machineId === simulationConfig?.machineId && isRunning) {
      setSessionStats(prev => ({ ...prev, samplesGenerated: prev.samplesGenerated + 1 }));
    }
  }, [simulationConfig?.machineId, isRunning]);

  useWebSocketSubscription({
    onSimulationStateUpdate: handleSimulationStateUpdate,
    onTelemetryUpdate: handleTelemetryUpdate
  });

  const handleConfigChange = (changes: Partial<SimulationConfig>) => {
    if (simulationConfig) setSimulationConfig({ ...simulationConfig, ...changes });
  };

  const handlePresetSelect = (preset: DemoPreset) => {
    setSimulationConfig(preset.config);
    // Auto-select machine if needed
    if (!selectedMachine && MOCK_MACHINES.length > 0) {
      setSelectedMachine(MOCK_MACHINES[0]);
    }
  };

  const handleStart = () => {
    if (!simulationConfig) return;

    const selectedId = selectedMachine?.sk.replace('MACHINE#', '');
    const configToSend = {
      ...simulationConfig,
      machineId: selectedId ?? simulationConfig.machineId,
      usageProfile: selectedMachine?.usageProfile ?? simulationConfig.usageProfile,
    };

    console.log('Starting simulation with config:', configToSend);
    sendMessage({ action: 'start_simulation', config: configToSend });
    setIsRunning(true);
    setSessionStats(prev => ({ ...prev, startTime: new Date().toISOString(), samplesGenerated: 0, eventsInjected: 0 }));
    subscribeToMachine(configToSend.machineId);
  };

  const handleStartFleet = () => {
    MOCK_MACHINES.forEach(machine => {
      const machineId = machine.sk.replace('MACHINE#', '');
      const config: SimulationConfig = {
        machineId,
        usageProfile: machine.usageProfile,
        intervalMs: 1000,
      };
      subscribeToMachine(machineId);
      sendMessage({ action: 'start_simulation', config });
    });
  };

  const handleStopFleet = () => {
    MOCK_MACHINES.forEach(machine => {
      const machineId = machine.sk.replace('MACHINE#', '');
      sendMessage({ action: 'stop_simulation', machineId });
    });
  };

  const handleStop = () => {
    if (simulationConfig) {
      setIsRunning(false); // Immediate visual feedback
      setSessionStats(prev => ({ ...prev, startTime: null }));
      sendMessage({ action: 'stop_simulation', machineId: simulationConfig.machineId });
    }
  };

  const handleInjectEvent = (eventType: string) => {
    if (simulationConfig) {
      sendMessage({ action: 'inject_event', machineId: simulationConfig.machineId, eventType });
      setSessionStats(prev => ({ ...prev, eventsInjected: prev.eventsInjected + 1 }));
    }
  };

  const getSessionDuration = () => {
    if (!sessionStats.startTime) return '00:00:00';
    const start = new Date(sessionStats.startTime);
    const now = new Date();
    const diff = now.getTime() - start.getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <DashboardContentShell>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">PLC Simulation Engine</h1>
        <p className="text-slate-400">Industrial-grade synthetic telemetry generation with configurable degradation patterns</p>
      </div>

      {/* KPI Bar */}
      <KPIBar
        metrics={[
          { label: 'Status', value: isRunning ? 'RUNNING' : 'STOPPED', status: isRunning ? 'good' : 'critical', icon: <Activity className="w-4 h-4" /> },
          { label: 'Machine', value: selectedMachine?.displayName || 'None', status: selectedMachine ? 'good' : 'warning' },
          { label: 'Duration', value: getSessionDuration(), status: 'good', icon: <Clock className="w-4 h-4" /> },
          { label: 'Samples', value: sessionStats.samplesGenerated, status: 'good' },
          { label: 'Events', value: sessionStats.eventsInjected, status: sessionStats.eventsInjected > 0 ? 'warning' : 'good' },
        ]}
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="xl:col-span-1">
          <DashboardSection title="Simulation Configuration" subtitle="Configure PLC data generation parameters">
            <div className="flex space-x-1 mb-6 bg-slate-900 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('manual')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'manual' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
              >
                Manual Config
              </button>
              <button
                onClick={() => setActiveTab('presets')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'presets' ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                }`}
              >
                Demo Presets
              </button>
            </div>

            {activeTab === 'manual' ? (
              <SimulationControls
                selectedMachine={selectedMachine}
                onMachineSelect={setSelectedMachine}
                simulationConfig={simulationConfig}
                onConfigChange={handleConfigChange}
                isRunning={isRunning}
                onStart={handleStart}
                onStop={handleStop}
                onInjectEvent={handleInjectEvent}
              />
            ) : (
              <div className="space-y-4">
                <DemoPresets
                  selectedMachine={selectedMachine}
                  onPresetSelect={handlePresetSelect}
                  currentConfig={simulationConfig}
                  isRunning={isRunning}
                />
                {selectedMachine && simulationConfig && (
                  <div className="pt-4 border-t border-slate-700">
                    <button
                      onClick={isRunning ? handleStop : handleStart}
                      disabled={!simulationConfig?.usageProfile}
                      className={`w-full flex items-center justify-center space-x-2 px-6 py-4 rounded-lg font-medium text-lg transition-all ${
                        isRunning
                          ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-500/25'
                          : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/25 disabled:bg-slate-700 disabled:cursor-not-allowed disabled:shadow-none'
                      }`}
                    >
                      {isRunning ? (
                        <><Square className="w-5 h-5" /> <span>Stop Simulation</span></>
                      ) : (
                        <><Play className="w-5 h-5" /> <span>Start Simulation</span></>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </DashboardSection>
        </div>

        {/* Telemetry Display */}
        <div className="xl:col-span-2">
          <DashboardWidget title="Real-Time Telemetry" className="h-full">
            <LiveTelemetry machineId={simulationConfig?.machineId || null} isRunning={isRunning} />
          </DashboardWidget>
        </div>
      </div>

      {/* Status Panel */}
      {selectedMachine && simulationConfig && (
        <DashboardSection title="Session Status" subtitle="Current simulation parameters and runtime information">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Machine</div>
              <div className="font-bold text-white text-sm">{selectedMachine.displayName}</div>
              <div className="text-xs text-slate-500 mt-1">{selectedMachine.designVariant}</div>
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Profile</div>
              <div className="font-bold text-white text-sm capitalize">{simulationConfig.usageProfile?.replace('_', ' ')}</div>
              {simulationConfig.degradationPattern && (
                <div className="text-xs text-slate-500 mt-1">Pattern {simulationConfig.degradationPattern}</div>
              )}
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Sample Rate</div>
              <div className="font-bold text-white text-sm">{simulationConfig.intervalMs}ms</div>
              <div className="text-xs text-slate-500 mt-1">{(1000/simulationConfig.intervalMs).toFixed(1)} Hz</div>
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Duration</div>
              <div className="font-bold text-white text-sm">
                {simulationConfig.duration ? `${Math.round(simulationConfig.duration / 60000)}m` : '∞'}
              </div>
              <div className="text-xs text-slate-500 mt-1">{simulationConfig.duration ? 'Limited' : 'Continuous'}</div>
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Runtime</div>
              <div className="font-bold text-white text-sm">{getSessionDuration()}</div>
              <div className={`text-xs mt-1 ${isRunning ? 'text-emerald-400' : 'text-slate-500'}`}>
                {isRunning ? 'Active' : 'Stopped'}
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Data Points</div>
              <div className="font-bold text-white text-sm">{sessionStats.samplesGenerated.toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-1">{sessionStats.eventsInjected} events</div>
            </div>
          </div>
        </DashboardSection>
      )}
    </DashboardContentShell>
  );
}